import { createCollection } from '@rocketlane/state-db';
import { StatusUpdatesAPI } from 'api/status-updates';
/**
 * The single source of truth for StatusUpdate entities. Replaces the
 * `statusUpdates` Redux slice, the Redux sagas, and the React Query detail
 * cache.
 *
 * Data enters via `push()` from:
 *   - list fetches (fetchers.fetchStatusUpdatesList)
 *   - detail fetches (fetchers.fetchStatusUpdateDetail)
 *   - mutation responses (onInsert / onUpdate handlers below)
 *   - WS/Pusher refetches (push the refreshed entity)
 *
 * StatusUpdate has one shape — the api shape IS the store shape — so there is
 * no `to-store.ts` transform layer and no dual-shape push risk.
 *
 * The `active` selection (which SU is open in the editor) is UI state, not
 * entity data. It lives in local component state as `activeStatusUpdateId:
 * number | null`; the entity itself is read via `useStatusUpdate(id)`.
 */ export const statusUpdatesCollection = createCollection({
    id: 'status-updates',
    getKey: (su)=>su.statusUpdateId,
    /**
   * Prefer server-set `updatedAt` as the staleness guard. An older WS snapshot
   * (lower `updatedAt`) cannot clobber a more-recent value already in the
   * collection. Active optimistic overlays are NOT affected by push regardless.
   */ isNewer: (incoming, existing)=>incoming.updatedAt >= existing.updatedAt,
    /**
   * Full update — sends the complete StatusUpdate entity to the server.
   * Callers express changes as `DeepPartial<StatusUpdate>` via
   * `collection.update(id, changes)`; the library shallow-merges into the
   * current entity before the handler receives `ctx.modified`.
   *
   * Note: publish (Draft → Published state transition) is a separate POST
   * endpoint (`/publish`) and must go through `actions.publishStatusUpdate`,
   * NOT through `collection.update({ state: Published })`.
   */ onUpdate: async (ctx)=>StatusUpdatesAPI.updateStatusUpdate(ctx.modified),
    /**
   * Create — `ctx.modified` is the full optimistic entity built by the caller
   * before passing to `collection.insert(optimisticEntity)`. The server
   * response carries the real `statusUpdateId` which the collection swaps for
   * the caller-supplied temp id.
   */ onInsert: async (ctx)=>StatusUpdatesAPI.createStatusUpdate(ctx.modified),
    /**
   * Delete — `ctx.modified` is the entity that was in the collection before
   * deletion. The handler fires the real DELETE; the collection removes the
   * entry on success.
   *
   * WS/Pusher-originated deletions and post-publish removals must use
   * `statusUpdatesCollection.externalDelete(id)` — NOT `collection.delete()` —
   * to avoid double-calling the API.
   */ onDelete: async (ctx)=>{
        await StatusUpdatesAPI.deleteStatusUpdate(ctx.modified);
    }
});
